import {useHistoryPageState} from '@aboutyou/history-page-state';
import {styled} from '@linaria/react';
import type {JSX} from 'react';
import React, {FC, createContext, useCallback, useMemo, useRef} from 'react';
import {useRectEffect} from 'react-viewport-utils';
import {COLORS} from '../../../constants/colors';
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
} from '../../../constants/customMedia';
import {GRID_GUTTER_WIDTH} from '../../../constants/gridSystem';

interface StaticGridProps {
  columns: number;
}

const GRID_GAP = '5px';

const BaseGrid = styled.ul`
  list-style-type: none;
  padding: 0;
  && {
    padding-inline: 0;
  }
  margin-top: 0.5rem;
  display: grid;
  grid-template-rows: auto;
  grid-gap: ${GRID_GAP};
  width: 100%;
`;

const StaticGrid = styled(BaseGrid)<StaticGridProps>`
  --grid-columns: ${props => props.columns};
  grid-template-columns: repeat(
    auto-fill,
    minmax(
      calc(
        (100% - (var(--grid-columns) - 1) * ${GRID_GAP}) / var(--grid-columns)
      ),
      1fr
    )
  );
`;

type ColumnConfiguration = {small: number; medium: number; large: number};

export interface FluidGridProps {
  columns?: ColumnConfiguration;
}
export const FluidGrid = styled(BaseGrid)<FluidGridProps>`
  --grid-columns: ${props => props.columns?.small ?? 2};

  grid-template-columns: repeat(
    auto-fill,
    minmax(
      calc(
        (100% - (var(--grid-columns) - 1) * ${GRID_GAP}) / var(--grid-columns)
      ),
      1fr
    )
  );

  &[data-enable-grid-layout='true'] {
    gap: ${GRID_GUTTER_WIDTH};
    grid-template-columns: repeat(
      auto-fill,
      calc(
        (100% - (var(--grid-columns) - 1) * ${GRID_GUTTER_WIDTH}) /
          var(--grid-columns)
      )
    );
  }

  @media ${mediaDesktopAndUp} {
    --grid-columns: ${props => props.columns?.medium ?? 3};
  }

  @media ${mediaLargeScreens} {
    --grid-columns: ${props => props.columns?.large ?? 4};
  }
`;

interface GridProps {
  columns?: number | ColumnConfiguration;
  children: React.ReactNode;
  enableEnhancedTileRendering?: false;
  className?: string;
  testId?: string;
  enableGridLayout?: boolean;
}

interface EnhancedGridMeasureProps
  extends Omit<GridProps, 'enableEnhancedTileRendering'> {
  enableEnhancedTileRendering: true;
  uniqueKey: string;
}

export const Grid = (
  props: (GridProps | EnhancedGridMeasureProps) & {
    ref?: React.Ref<HTMLUListElement>;
  },
) => {
  const {ref} = props;
  const grid =
    props.columns === undefined || typeof props.columns !== 'number' ? (
      <FluidGrid
        ref={ref}
        columns={props.columns as ColumnConfiguration}
        data-testid={props.testId}
        className={props.className}
        data-enable-grid-layout={props.enableGridLayout}
      >
        {props.children}
      </FluidGrid>
    ) : (
      <StaticGrid
        ref={ref}
        className={props.className}
        columns={props.columns}
        data-testid={props.testId}
      >
        {props.children}
      </StaticGrid>
    );

  if (props.enableEnhancedTileRendering) {
    return (
      <EnhancedGridMeasure
        data-testid={props.testId}
        className={props.className}
        uniqueKey={(props as EnhancedGridMeasureProps).uniqueKey}
      >
        {grid}
      </EnhancedGridMeasure>
    );
  }
  return grid;
};

const createGridArea = ({
  position,
  columns,
}: {
  position: number;
  columns: number;
}) => {
  return {
    row: {
      start: position / columns + 1,
      end: position / columns + 2,
    },
    column: {
      start: 1,
      end: columns + 1,
    },
  };
};

const createGridAreaStyle = ({
  position,
  columns,
}: {
  position: number;
  columns: number;
}) => {
  const gridArea = createGridArea({position, columns});

  return {
    gridArea: `${Math.ceil(gridArea.row.start)} / ${Math.ceil(
      gridArea.column.start,
    )} / ${Math.ceil(gridArea.row.end)} / ${Math.ceil(gridArea.column.end)}`,
  };
};

const StaticSlot = styled.div`
  background-color: ${COLORS.White};
`;

const FluidSlot = styled.div<{position: number}>`
  background-color: ${COLORS.White};

  --grid-area: ${({position}) =>
    createGridAreaStyle({columns: 2, position}).gridArea};

  @media ${mediaDesktopAndUp} {
    --grid-area: ${({position}) =>
      createGridAreaStyle({columns: 3, position}).gridArea};
  }

  @media ${mediaLargeScreens} {
    --grid-area: ${({position}) =>
      createGridAreaStyle({columns: 4, position}).gridArea};
  }

  grid-area: var(--grid-area);
`;

export const Slot = ({
  columns,
  position,
  children,
  className,
  dataTestid,
  Comp,
  ref,
}: {
  columns?: number;
  position: number;
  children?: React.ReactNode;
  className?: string;
  dataTestid?: string;
  Comp?: keyof JSX.IntrinsicElements;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return columns === undefined ? (
    <FluidSlot
      ref={ref}
      position={position}
      className={className}
      data-testid={dataTestid}
      as={Comp}
    >
      {children}
    </FluidSlot>
  ) : (
    <StaticSlot
      ref={ref}
      className={className}
      style={createGridAreaStyle({columns, position})}
      data-testid={dataTestid}
      as={Comp}
    >
      {children}
    </StaticSlot>
  );
};

const EnhancedGridMeasure: FC<
  React.PropsWithChildren<{uniqueKey: string; className?: string}>
> = ({children, className, uniqueKey}) => {
  // `useHistoryPageState` makes sure the state is persisted on history back when the component is mounted again (as the useRectEffect would only render afterwards which would crash the scroll restoration)
  const [width, setWidth] = useHistoryPageState<number | undefined>(undefined, {
    uniqueKey: `@ui-components/EnhancedGridMeasure${uniqueKey}`,
  });
  // the cache is used to persist the width when the components don't remount but navigation changes. E.g. when navigation from one page to another. In that case the `useHistoryPageState` would update to `undefined` which is unintended for the grid
  const widthCache = useRef(width);
  const ref = useRef<HTMLDivElement>(null);
  useRectEffect(
    useCallback(
      rect => {
        setWidth(rect?.width);
        widthCache.current = rect?.width;
      },
      [setWidth],
    ),
    ref,
    {disableScrollUpdates: true},
  );
  const style =
    widthCache.current !== undefined
      ? ({
          '--grid-width': `${widthCache.current}px`,
          '--grid-gap': GRID_GAP,
        } as React.CSSProperties)
      : {};

  const context: EnhancedGridMeasureContextType = useMemo(
    () => ({
      width: widthCache.current,
      // when the provider is implemented it's always available
      available: true,
    }),
    [widthCache.current],
  );

  return (
    <EnhancedGridMeasureContext.Provider value={context}>
      <div
        ref={ref}
        style={style}
        data-testid="enhancedGridMeasure"
        className={className}
      >
        {children}
      </div>
    </EnhancedGridMeasureContext.Provider>
  );
};

interface DisabledState {
  available: false;
  width: undefined;
}

interface EnabledIdleState {
  available: true;
  width: undefined;
}

interface ActiveState {
  available: true;
  width: number;
}

type EnhancedGridMeasureContextType =
  | DisabledState
  | EnabledIdleState
  | ActiveState;

const EnhancedGridMeasureContext =
  createContext<EnhancedGridMeasureContextType>({
    available: false,
    width: undefined,
  } as DisabledState);
